import * as Dialog from "@radix-ui/react-dialog";
import { X } from "lucide-react";

import type { TestCase } from "@/api/tests-api";
import { TestCaseViewer } from "@/components/tests/TestCaseViewer";

/**
 * A library case, shown the way a candidate will see it.
 *
 * Deliberately the SAME `TestCaseViewer` the exam uses, reading the same
 * stored JPEGs — not a second preview built alongside it. A preview that
 * renders differently from the real thing is worse than none, because it
 * invites sign-off on something nobody has actually looked at.
 *
 * The brief above the images is the same text the candidate is given,
 * which is the other half of the check: whoever assembled the case can
 * confirm the history reads correctly against the pictures before any
 * candidate is assigned it.
 *
 * The only additions are administrative — what the candidate never sees:
 * how many exams use it, and how many candidates already hold it.
 */
export function CasePreviewModal({
  testCase,
  onClose,
}: {
  testCase: TestCase | null;
  onClose: () => void;
}) {
  if (!testCase) return null;

  return (
    <Dialog.Root
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50" />
        <Dialog.Content
          data-cy="case-preview-modal"
          className="fixed left-1/2 top-1/2 z-50 flex max-h-[92vh] w-full max-w-5xl -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-xl border border-border bg-background-card shadow-theme-lg"
        >
          <div className="flex items-start justify-between gap-3 border-b border-border px-6 pt-5 pb-4">
            <div className="min-w-0">
              <Dialog.Title className="text-lg font-semibold text-foreground-heading">
                {testCase.title}
              </Dialog.Title>
              <Dialog.Description className="mt-1 text-xs text-foreground-muted">
                {[testCase.modality, testCase.body_part]
                  .filter(Boolean)
                  .join(" · ") || "No modality recorded"}
                {" · "}
                {testCase.instance_count} image
                {testCase.instance_count === 1 ? "" : "s"}
                {testCase.exam_count > 0 &&
                  ` · in ${testCase.exam_count} exam${
                    testCase.exam_count === 1 ? "" : "s"
                  }`}
                {testCase.assigned_count > 0 &&
                  ` · ${testCase.assigned_count} candidate${
                    testCase.assigned_count === 1 ? "" : "s"
                  } hold it`}
              </Dialog.Description>
            </div>
            <Dialog.Close
              data-cy="btn-close-case-preview"
              aria-label="Close"
              className="rounded-md p-1 text-foreground-muted hover:bg-table-row-hover hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            >
              <X className="h-4 w-4" />
            </Dialog.Close>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
            {testCase.clinical_history ? (
              <div className="mb-4 rounded-lg border border-border bg-background px-4 py-3">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-foreground-muted">
                  The brief, as the candidate reads it
                </p>
                <p className="mt-1 text-sm text-foreground">
                  {testCase.clinical_history}
                </p>
              </div>
            ) : (
              <p
                data-cy="case-preview-no-history"
                className="mb-4 text-xs text-status-urgent"
              >
                This case has no clinical history — a candidate will be shown
                the images with no context at all.
              </p>
            )}

            {/* 70vh, NOT 55. The image is what this modal is for, and
                55vh left the stage about 150px tall once the series list
                and the filmstrip had taken their share -- a CT slice
                rendered smaller than its own thumbnail. With the series
                list moved to the left there is height to give back. */}
            <div className="h-[70vh] min-h-[420px]">
              <TestCaseViewer source={{ kind: "library", caseId: testCase.id }} />
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
